// ConnectModal — real, live Connect action for an Ecosystem profile card.
// Submits a genuine connection request via connectEcosystemProfile (POST
// /ecosystem/connect), which writes to ECOSYSTEM_CONNECTIONS_TABLE and
// notifies the target profile's owner. Renders whatever the backend
// actually returns — never a fabricated success (P7: no dead/fake actions).
import { useState } from 'react'
import { X, Send, Loader2, CheckCircle2, AlertCircle } from 'lucide-react'
import { connectEcosystemProfile } from '../../services/api'
import type { EcosystemProfile } from '../../types'

interface ConnectModalProps {
  profile: EcosystemProfile
  onClose: () => void
}

function extractErrorMessage(err: unknown): string {
  const apiError = (err as { response?: { data?: { error?: string } } })?.response?.data?.error
  if (apiError) return apiError
  const message = (err as Error)?.message
  return message || 'Failed to send the connection request. Please try again.'
}

export default function ConnectModal({ profile, onClose }: ConnectModalProps) {
  const [message, setMessage] = useState('')
  const [sending, setSending] = useState(false)
  const [success, setSuccess] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const handleSend = async () => {
    setSending(true)
    setErrorMessage(null)
    try {
      await connectEcosystemProfile(profile.profile_id, message)
      setSuccess(true)
    } catch (err) {
      setErrorMessage(extractErrorMessage(err))
    } finally {
      setSending(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" role="dialog" aria-modal="true" aria-label={`Connect with ${profile.name}`}>
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-iflow-900">Connect with {profile.name}</h3>
          <button onClick={onClose} className="p-1 text-gray-500 hover:text-gray-600" aria-label="Close">
            <X size={18} />
          </button>
        </div>

        {success ? (
          <div className="flex flex-col items-center gap-2 py-6 text-center" data-testid="connect-success">
            <CheckCircle2 className="w-8 h-8 text-emerald-500" />
            <p className="text-sm text-gray-700">Connection request sent to {profile.name}.</p>
            <button
              onClick={onClose}
              className="mt-3 px-4 py-2 text-sm text-white bg-iflow-700 rounded-lg hover:bg-iflow-800"
            >
              Done
            </button>
          </div>
        ) : (
          <>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 light-input text-sm outline-none resize-none"
                  placeholder={`Introduce yourself to ${profile.name} and explain why you'd like to connect...`}
                />
              </div>
            </div>

            {errorMessage && (
              <p data-testid="connect-error" className="mt-4 flex items-start gap-1.5 text-sm text-red-600">
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" /> {errorMessage}
              </p>
            )}

            <div className="flex justify-end gap-3 mt-6">
              <button onClick={onClose} className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50">
                Cancel
              </button>
              <button
                onClick={handleSend}
                disabled={sending}
                className="flex items-center gap-1.5 px-4 py-2 text-sm text-white bg-iflow-700 rounded-lg hover:bg-iflow-800 disabled:opacity-50"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send size={14} />}
                {sending ? 'Sending...' : 'Send Request'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
